import { useEffect, useRef, useState, useCallback } from 'react';
import { Pencil, Type, Undo2, Redo2, Check, X, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { Capacitor } from '@capacitor/core';

interface ImageEditorProps {
  src: string;
  fileName: string;
  mimeType: string;
  onSave: (file: File, dataUrl: string) => void;
  onCancel: () => void;
}

interface Point {
  x: number;
  y: number;
}

type EditorAction =
  | { type: 'stroke'; points: Point[]; color: string; size: number }
  | { type: 'text'; x: number; y: number; text: string; color: string; fontSize: number };

// Conservative caps that keep the canvas backing store within mobile WebView / Safari limits.
// Each canvas costs width*height*4 bytes; we keep two (display + committed cache) plus a transient
// export buffer, so we cap area at ~16.7Mpx and the longest side at 4096px.
const MAX_SIDE = 4096;
const MAX_AREA = 4096 * 4096;

function clampToCanvasLimits(w: number, h: number): { width: number; height: number; scale: number } {
  const scale = Math.min(1, MAX_SIDE / Math.max(w, h), Math.sqrt(MAX_AREA / (w * h)));
  if (scale >= 1) return { width: w, height: h, scale: 1 };
  return { width: Math.round(w * scale), height: Math.round(h * scale), scale };
}

const COLORS = ['#ef4444', '#f59e0b', '#22c55e', '#3b82f6', '#000000', '#ffffff'];
const SIZES = [3, 6, 12];

function drawStroke(ctx: CanvasRenderingContext2D, stroke: Extract<EditorAction, { type: 'stroke' }>) {
  const { points, color, size } = stroke;
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = size;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  if (points.length === 1) {
    ctx.beginPath();
    ctx.arc(points[0].x, points[0].y, size / 2, 0, Math.PI * 2);
    ctx.fill();
    return;
  }
  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);
  for (let i = 1; i < points.length; i++) {
    ctx.lineTo(points[i].x, points[i].y);
  }
  ctx.stroke();
}

function drawTextAction(ctx: CanvasRenderingContext2D, t: Extract<EditorAction, { type: 'text' }>) {
  ctx.fillStyle = t.color;
  ctx.textBaseline = 'top';
  ctx.font = `${t.fontSize}px Heebo, sans-serif`;
  ctx.fillText(t.text, t.x, t.y);
}

export default function ImageEditor({ src, fileName, mimeType, onSave, onCancel }: ImageEditorProps) {
  const displayCanvasRef = useRef<HTMLCanvasElement>(null);
  const committedCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const baseImageRef = useRef<HTMLImageElement | null>(null);

  const drawingRef = useRef(false);
  const currentStrokeRef = useRef<Extract<EditorAction, { type: 'stroke' }> | null>(null);
  const ignoreNextBlurRef = useRef(false);

  const [ready, setReady] = useState(false);
  const [actions, setActions] = useState<EditorAction[]>([]);
  const [redoStack, setRedoStack] = useState<EditorAction[]>([]);
  const [tool, setTool] = useState<'draw' | 'text'>('draw');
  const [color, setColor] = useState(COLORS[0]);
  const [size, setSize] = useState(SIZES[1]);
  const [fontSize, setFontSize] = useState(32);
  const [pendingText, setPendingText] = useState<{ displayX: number; displayY: number; x: number; y: number } | null>(null);
  const [textValue, setTextValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const isDirty = actions.length > 0;

  // Paint the offscreen committed canvas (base image + all committed actions).
  const rebuildCommitted = useCallback((list: EditorAction[]) => {
    const committed = committedCanvasRef.current;
    const base = baseImageRef.current;
    if (!committed || !base) return;
    const ctx = committed.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, committed.width, committed.height);
    ctx.drawImage(base, 0, 0, committed.width, committed.height);
    for (const action of list) {
      if (action.type === 'stroke') drawStroke(ctx, action);
      else drawTextAction(ctx, action);
    }
  }, []);

  // Blit the committed canvas to the visible canvas, then draw the in-progress stroke on top.
  const renderDisplay = useCallback(() => {
    const display = displayCanvasRef.current;
    const committed = committedCanvasRef.current;
    if (!display || !committed) return;
    const ctx = display.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, display.width, display.height);
    ctx.drawImage(committed, 0, 0);
    if (currentStrokeRef.current) drawStroke(ctx, currentStrokeRef.current);
  }, []);

  // Load the source image and size the canvases (downscaling only if it exceeds limits).
  useEffect(() => {
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      const { width, height, scale } = clampToCanvasLimits(img.naturalWidth, img.naturalHeight);
      const committed = document.createElement('canvas');
      committed.width = width;
      committed.height = height;
      committedCanvasRef.current = committed;
      baseImageRef.current = img;

      const display = displayCanvasRef.current;
      if (display) {
        display.width = width;
        display.height = height;
      }
      rebuildCommitted([]);
      renderDisplay();
      setReady(true);

      if (scale < 1) {
        toast({ title: 'התמונה הוקטנה לצורך עריכה' });
      }
    };
    img.onerror = () => {
      toast({ title: 'שגיאה בטעינת התמונה', variant: 'destructive' });
      onCancel();
    };
    img.src = src;
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  // Re-render whenever the committed action list changes.
  useEffect(() => {
    if (!ready) return;
    rebuildCommitted(actions);
    renderDisplay();
  }, [actions, ready, rebuildCommitted, renderDisplay]);

  const toCanvasPoint = (e: React.PointerEvent<HTMLCanvasElement>): Point & { displayX: number; displayY: number } => {
    const canvas = displayCanvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const displayX = e.clientX - rect.left;
    const displayY = e.clientY - rect.top;
    return {
      displayX,
      displayY,
      x: displayX * (canvas.width / rect.width),
      y: displayY * (canvas.height / rect.height),
    };
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    console.log(ready);
    
    if (!ready) return;
    const pt = toCanvasPoint(e);
    if (tool === 'text') {
      e.preventDefault();

      ignoreNextBlurRef.current = true;

      setTextValue('');
      setPendingText({
        displayX: pt.displayX,
        displayY: pt.displayY,
        x: pt.x,
        y: pt.y,
      });

      return;
    }
    
    // draw
    e.currentTarget.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    currentStrokeRef.current = { type: 'stroke', points: [{ x: pt.x, y: pt.y }], color, size };
    renderDisplay();
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current || !currentStrokeRef.current) return;
    const pt = toCanvasPoint(e);
    currentStrokeRef.current.points.push({ x: pt.x, y: pt.y });
    renderDisplay();
  };

  const finishStroke = () => {
    if (!drawingRef.current || !currentStrokeRef.current) return;
    const stroke = currentStrokeRef.current;
    drawingRef.current = false;
    currentStrokeRef.current = null;
    setActions((prev) => [...prev, stroke]);
    setRedoStack([]);
  };

  const commitText = (fromBlur) => {
    // if(Capacitor.isNativePlatform()) {
    //   if(fromBlur && !blurCounter) {
    //     setBlurCounter(1);
    //     return;
    //   }else if(fromBlur) {
    //     setBlurCounter(0);
    //   }
    // }
    const value = textValue.trim();
    if (pendingText && value) {
      const action: EditorAction = {
        type: 'text',
        x: pendingText.x,
        y: pendingText.y,
        text: value,
        color,
        fontSize,
      };
      setActions((prev) => [...prev, action]);
      setRedoStack([]);
    }
    setPendingText(null);
    setTextValue('');
  };

  const cancelText = () => {
    setPendingText(null);
    setTextValue('');
  };

  const handleUndo = () => {
    if (actions.length === 0) return;
    setActions((prev) => {
      const next = prev.slice(0, -1);
      setRedoStack((r) => [...r, prev[prev.length - 1]]);
      return next;
    });
  };

  const handleRedo = () => {
    if (redoStack.length === 0) return;
    setRedoStack((prev) => {
      const action = prev[prev.length - 1];
      setActions((a) => [...a, action]);
      return prev.slice(0, -1);
    });
  };

  const handleSave = () => {
    const display = displayCanvasRef.current;
    const committed = committedCanvasRef.current;
    if (!display || !committed) return;
    setIsSaving(true);
    // Export the committed layer (display may contain a half-finished stroke during pointer events).
    const outType = mimeType.startsWith('image/') ? mimeType : 'image/png';
    committed.toBlob(
      (blob) => {
        if (!blob) {
          setIsSaving(false);
          toast({ title: 'שגיאה בשמירת התמונה', variant: 'destructive' });
          return;
        }
        const file = new File([blob], fileName, { type: blob.type });
        const reader = new FileReader();
        reader.onload = () => {
          onSave(file, reader.result as string);
        };
        reader.onerror = () => {
          setIsSaving(false);
          toast({ title: 'שגיאה בשמירת התמונה', variant: 'destructive' });
        };
        reader.readAsDataURL(blob);
      },
      outType,
      0.92,
    );
  };

  const handleCancel = () => {
    if (isDirty) {
      setShowConfirm(true);
      return;
    }
    onCancel();
  };

  return (
    <div className="fixed inset-0 z-[50] bg-black flex flex-col" dir="rtl">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 bg-black/80 text-white shrink-0">
        <button
          onClick={handleCancel}
          disabled={isSaving}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg hover:bg-white/10 transition-colors disabled:opacity-50"
        >
          <X className="w-5 h-5" />
          <span className="text-sm font-medium">ביטול</span>
        </button>
        <span className="text-sm font-medium">עריכת תמונה</span>
        <button
          onClick={handleSave}
          disabled={isSaving || !ready}
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {isSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
          <span className="text-sm">שמירה</span>
        </button>
      </div>

      {/* Canvas area */}
      <div className="flex-1 relative overflow-hidden flex items-center justify-center p-2">
        {!ready && <Loader2 className="w-8 h-8 animate-spin text-white" />}
        <div className="relative max-w-full max-h-full">
          <canvas
            ref={displayCanvasRef}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={finishStroke}
            onPointerCancel={finishStroke}
            onPointerLeave={finishStroke}
            className={cn('max-w-full max-h-[calc(100vh-9rem)] object-contain', tool === 'text' ? 'cursor-text' : 'cursor-crosshair')}
            style={{ touchAction: 'none', display: ready ? 'block' : 'none' }}
          />
          {/* Inline text input positioned at the tapped location */}
          {pendingText && (
            <input
              autoFocus
              value={textValue}
              onChange={(e) => setTextValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitText(false);
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  cancelText();
                }
              }}
              onBlur={() => {
                if (ignoreNextBlurRef.current) {
                  ignoreNextBlurRef.current = false;
                  return;
                }

                commitText(true);
              }}
              placeholder="הקלד טקסט..."
              className="absolute bg-white/90 text-black text-sm rounded px-2 py-1 outline-none border border-primary shadow-lg"
              style={{ left: pendingText.displayX, top: pendingText.displayY, maxWidth: '60%' }}
            />
          )}
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-black/80 text-white px-4 py-3 shrink-0 space-y-3">
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() =>{ setTool('draw'); setPendingText(null); }}
            className={cn('flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors', tool === 'draw' ? 'bg-primary text-primary-foreground' : 'bg-white/10 hover:bg-white/20')}
          >
            <Pencil className="w-4 h-4" />
            ציור
          </button>
          <button
            onClick={() => setTool('text')}
            className={cn('flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors', tool === 'text' ? 'bg-primary text-primary-foreground' : 'bg-white/10 hover:bg-white/20')}
          >
            <Type className="w-4 h-4" />
            טקסט
          </button>
          <div className="w-px h-6 bg-white/20 mx-1" />
          <button
            onClick={handleUndo}
            disabled={actions.length === 0}
            className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors disabled:opacity-40 disabled:hover:bg-white/10"
            aria-label="בטל"
          >
            <Undo2 className="w-4 h-4" />
          </button>
          <button
            onClick={handleRedo}
            disabled={redoStack.length === 0}
            className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors disabled:opacity-40 disabled:hover:bg-white/10"
            aria-label="בצע שוב"
          >
            <Redo2 className="w-4 h-4" />
          </button>
        </div>

        <div className="flex items-center justify-center gap-3">
          {/* Colors */}
          <div className="flex items-center gap-1.5">
            {COLORS.map((c) => (
              <button
                key={c}
                onClick={() => setColor(c)}
                className={cn('w-6 h-6 rounded-full border-2 transition-transform', color === c ? 'border-white scale-110' : 'border-white/30')}
                style={{ backgroundColor: c }}
                aria-label={`צבע ${c}`}
              />
            ))}
          </div>
          <div className="w-px h-6 bg-white/20" />
          {/* Sizes (stroke width; text size scales with it) */}
          <div className="flex items-center gap-1.5">
            {SIZES.map((s) => (
              <button
                key={s}
                onClick={() => {
                  setSize(s);
                  setFontSize(s * 6 + 14);
                }}
                className={cn('w-8 h-8 rounded-lg flex items-center justify-center transition-colors', size === s ? 'bg-primary' : 'bg-white/10 hover:bg-white/20')}
                aria-label={`עובי ${s}`}
              >
                <span className="rounded-full bg-white block" style={{ width: s + 2, height: s + 2 }} />
              </button>
            ))}
          </div>
        </div>
      </div>

      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent dir="rtl">
          <AlertDialogHeader>
            <AlertDialogTitle>יש שינויים שלא נשמרו</AlertDialogTitle>
            <AlertDialogDescription>האם לצאת בלי לשמור את העריכה?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>חזור לעריכה</AlertDialogCancel>
            <AlertDialogAction onClick={onCancel}>צא בלי לשמור</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
